import { useState, useEffect, useCallback } from 'react';
import type { EnglishLesson, LessonProgress } from '../../lib/types';
import { getAllProgress } from '../../lib/db';
import { englishLessons } from './data';
import EnglishLessonView from './EnglishLessonView';

const typeLabels: Record<EnglishLesson['type'], string> = {
  vocab: 'واژگان',
  grammar: 'گرامر',
  reading: 'خواندن',
  writing: 'نوشتن',
};

const typeIcons: Record<EnglishLesson['type'], string> = {
  vocab: 'A',
  grammar: '\u2699',
  reading: 'R',
  writing: '\u270D',
};

type GroupKey = EnglishLesson['type'];

function groupLessons(lessons: EnglishLesson[]): Record<GroupKey, EnglishLesson[]> {
  const groups: Record<GroupKey, EnglishLesson[]> = {
    vocab: [],
    grammar: [],
    reading: [],
    writing: [],
  };
  for (const lesson of lessons) {
    groups[lesson.type].push(lesson);
  }
  return groups;
}

export default function EnglishModule() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [activeLesson, setActiveLesson] = useState<EnglishLesson | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProgress = useCallback(async () => {
    try {
      const allProgress: LessonProgress[] = await getAllProgress();
      const englishIds = new Set(
        allProgress
          .filter((p) => p.moduleId === 'english')
          .map((p) => p.lessonId)
      );
      setCompletedIds(englishIds);
    } catch {
      // Proceed with empty progress on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  function handleComplete(lessonId: string) {
    setCompletedIds((prev) => new Set(prev).add(lessonId));
    // Navigate to next lesson or back to list
    const currentIndex = englishLessons.findIndex((l) => l.id === lessonId);
    const next = englishLessons[currentIndex + 1];
    if (next) {
      setActiveLesson(next);
    } else {
      setActiveLesson(null);
    }
  }

  function handleBack() {
    setActiveLesson(null);
  }

  // Find the first incomplete lesson as recommended
  const recommendedId = englishLessons.find((l) => !completedIds.has(l.id))?.id ?? null;

  const completedCount = englishLessons.filter((l) => completedIds.has(l.id)).length;
  const totalCount = englishLessons.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Lesson view
  if (activeLesson) {
    return (
      <EnglishLessonView
        lesson={activeLesson}
        onBack={handleBack}
        onComplete={handleComplete}
      />
    );
  }

  // Module listing
  if (loading) {
    return <div className="page english-loading">در حال بارگذاری...</div>;
  }

  const groups = groupLessons(englishLessons);

  return (
    <div className="page english-module">
      {/* Header */}
      <h1 className="page-title">آموزش انگلیسی</h1>
      <p className="english-module-subtitle">
        آمادگی IELTS قدم به قدم — از پایه شروع کنید
      </p>

      {/* Progress overview */}
      <div className="card english-progress-card">
        <div className="english-progress-header">
          <span className="english-progress-label">پیشرفت کلی</span>
          <span className="english-progress-value">{progressPercent}٪</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <p className="english-progress-detail">
          {completedCount} از {totalCount} درس تکمیل شده
        </p>
      </div>

      {/* Grouped lessons */}
      {(Object.keys(groups) as GroupKey[]).map((groupKey) => {
        const lessons = groups[groupKey];
        if (lessons.length === 0) return null;

        return (
          <section key={groupKey} className="english-group">
            <h2 className="english-group-title">
              <span className="english-group-icon">{typeIcons[groupKey]}</span>
              {typeLabels[groupKey]}
            </h2>

            <div className="english-lesson-list">
              {lessons.map((lesson) => {
                const completed = completedIds.has(lesson.id);
                const recommended = lesson.id === recommendedId;

                return (
                  <button
                    key={lesson.id}
                    className={
                      'card english-lesson-card' +
                      (completed ? ' lesson-completed' : '') +
                      (recommended ? ' lesson-recommended' : '')
                    }
                    onClick={() => setActiveLesson(lesson)}
                  >
                    <div className="lesson-card-row">
                      <div className="lesson-card-status">
                        {completed ? (
                          <span className="status-done" aria-label="تکمیل شده">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        ) : recommended ? (
                          <span className="status-recommended" aria-label="پیشنهادی">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          </span>
                        ) : (
                          <span className="status-pending">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-border)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          </span>
                        )}
                      </div>

                      <div className="lesson-card-info">
                        <span className="lesson-card-title-fa">{lesson.titleFa}</span>
                        <span className="lesson-card-title-en ltr">{lesson.title}</span>
                      </div>

                      <svg className="lesson-card-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                    </div>

                    {recommended && !completed && (
                      <span className="recommended-badge">پیشنهادی</span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
